import type {
  SearchContentParams,
  SearchContentResponse,
  SentimentAnalysisResult,
  UserKarma,
} from './types';
import type { ApiDomainDeps } from './domainDeps';

export function createMarketDomain(deps: ApiDomainDeps) {
  return {
    async getUserKarma(username: string): Promise<UserKarma> {
      return deps.request<UserKarma>(`/api/users/${username}`);
    },

    async analyzeSentiment(text: string): Promise<SentimentAnalysisResult> {
      return deps.request<SentimentAnalysisResult>('/api/analyze/sentiment', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
    },

    async getMetrics(): Promise<string> {
      const response = await fetch(`${deps.apiBaseUrl}/metrics`);
      return response.text();
    },

    async getStockPrice(ticker: string): Promise<{
      ticker: string;
      price: number;
      currency: string;
      timestamp: string;
      source: string;
    }> {
      return deps.request(`/api/stocks/${ticker.toUpperCase()}/price`);
    },

    async getStockChange(ticker: string, days: number = 1): Promise<{
      ticker: string;
      current_price: number;
      previous_price: number;
      change: number;
      change_percent: number;
      period_days: number;
    }> {
      return deps.request(`/api/stocks/${ticker.toUpperCase()}/change?days=${days}`);
    },

    async getStockInfo(ticker: string): Promise<{
      ticker: string;
      company_name: string;
      sector: string;
      industry: string;
      market_cap: number;
      pe_ratio: number;
      dividend_yield: number;
    }> {
      return deps.request(`/api/stocks/${ticker.toUpperCase()}/info`);
    },

    async getMultipleStockPrices(tickers: string[]): Promise<Array<{
      ticker: string;
      price: number;
      change: number;
      change_percent: number;
    }>> {
      const tickersParam = tickers.join(',');
      return deps.request(`/api/stocks/prices?tickers=${tickersParam}`);
    },

    async searchContent(params: SearchContentParams): Promise<SearchContentResponse> {
      const searchParams = new URLSearchParams();

      if (params.query) searchParams.append('query', params.query);
      if (params.ticker) searchParams.append('ticker', params.ticker);
      if (params.sentiment) searchParams.append('sentiment', params.sentiment);
      if (params.prediction) searchParams.append('prediction', params.prediction);
      if (params.author) searchParams.append('author', params.author);
      if (params.content_type) searchParams.append('content_type', params.content_type);
      if (params.date_from) searchParams.append('date_from', params.date_from);
      if (params.date_to) searchParams.append('date_to', params.date_to);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset) searchParams.append('offset', params.offset.toString());
      if (params.sort_by) searchParams.append('sort_by', params.sort_by);
      if (params.sort_order) searchParams.append('sort_order', params.sort_order);

      const queryString = searchParams.toString();
      const endpoint = `/api/search${queryString ? `?${queryString}` : ''}`;

      return deps.request<SearchContentResponse>(endpoint);
    },

    async searchByTicker(ticker: string, limit: number = 50): Promise<SearchContentResponse> {
      return this.searchContent({
        ticker: ticker.toUpperCase(),
        limit,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },

    async searchBySentiment(sentiment: 'positive' | 'negative' | 'neutral', limit: number = 50): Promise<SearchContentResponse> {
      return this.searchContent({
        sentiment,
        limit,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },

    async searchByPrediction(prediction: 'bullish' | 'bearish' | 'neutral', limit: number = 50): Promise<SearchContentResponse> {
      return this.searchContent({
        prediction,
        limit,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },

    async searchByAuthor(author: string, limit: number = 50): Promise<SearchContentResponse> {
      return this.searchContent({
        author,
        limit,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },
  };
}
